import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/api/client';
import {
  PERMISSIONS,
  type Account,
  type AccountDailyActivity,
  type AccountDetail,
  type AccountUsage,
  type AccountUser,
} from '@/api/types';
import { hasPermission } from '@/auth/session';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { ConfigurationPanel, TeamPanel } from '@/components/collaboration';
import { HealthPanel, OnboardingPanel, RiskPanel } from '@/components/scoring';
import { FlowsPanel } from '@/components/flows';
import {
  DeadSeatsPanel,
  DeviceMixPanel,
  LiveDot,
  PresenceStrip,
} from '@/components/presence';
import { StatTile } from '@/components/StatTile';
import { TrendChart, formatCompact } from '@/components/TrendChart';
import {
  SeatsCell,
  StatusBadge,
  formatMoney,
  formatTrialCountdown,
} from '@/components/subscription';
import { AccountBillingTab } from './AccountBillingTab';
import { formatWhen } from './AccountsPage';

/**
 * `/accounts/:tenantId` — since the 2026-09-30 redesign a tenant has ONE page, `/ops/tenants/:env/:id`, whose Usage,
 * People and Health tabs are the sections below. This route forwards there when the replica knows the tenant's
 * environment; a row collected before environments existed still renders here, with the same sections.
 *
 * Everything below derives from the tenant's own `audit_events` log, which records every action with an app and an
 * actor — so "which user used which feature, how much" is a group-by, not a feature that had to be built.
 */
export function AccountDetailPage() {
  const { tenantId = '' } = useParams();
  const [tab, setTab] = useState<'usage' | 'people' | 'health' | 'billing'>('usage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['account', tenantId],
    queryFn: () => api.account(tenantId),
    retry: false,
  });

  if (isLoading) return <div className="empty-state"><span className="spinner" /></div>;

  if (isError) {
    return (
      <Callout tone="error" title="Account not found">
        {error instanceof ApiError && error.status === 404
          ? 'No such account in the replica. Run the collector to refresh.'
          : error instanceof ApiError ? error.message : 'Unexpected error.'}
      </Callout>
    );
  }

  if (!data) return null;

  const { account } = data;
  if (account.environment && hasPermission(PERMISSIONS.hostsRead)) {
    return <Navigate to={`/ops/tenants/${encodeURIComponent(account.environment)}/${tenantId}?tab=usage`} replace />;
  }

  return (
    <>
      <div>
        <Link to="/accounts" className="t-label" style={{ color: 'var(--primary)' }}>← All accounts</Link>
        <h1 className="t-display" style={{ margin: '8px 0 4px' }}>
          {account.name || '(unnamed)'}
          {account.is_internal && <span className="badge-muted">internal</span>}
        </h1>
        <div className="t-label muted" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusBadge subscription={account.subscription} />
          <span>
            {account.owner_email || 'no owner recorded'} · {account.use_type} · created{' '}
            {formatWhen(account.created_at)} · synced {formatWhen(account.synced_at)}
          </span>
        </div>
      </div>

      <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
        {/* Billing reads the subscription (subscription:read) — a viewer's call would be refused. */}
        {(['usage', 'people', 'health', 'billing'] as const)
          .filter((key) => key !== 'billing' || hasPermission(PERMISSIONS.subscriptionRead))
          .map((key) => (
          <button key={key} type="button" className={`btn ${tab === key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(key)}>
            {key === 'usage' ? 'Usage' : key === 'people' ? 'People' : key === 'health' ? 'Health' : 'Billing'}
            {key === 'people' && data.users && data.users.length > 0 && <span className="badge-muted">{data.users.length}</span>}
          </button>
        ))}
      </div>

      {tab === 'billing' && hasPermission(PERMISSIONS.subscriptionRead) ? (
        <>
          {!account.environment && (
            <Callout tone="info" title="No environment on record for this tenant">
              The saved copy predates environments, so this panel asks the console&apos;s own IAM connection. Once the
              collector places the tenant on an environment, this page opens the full tenant page instead.
            </Callout>
          )}
          <AccountBillingTab tenantId={tenantId} environment={account.environment ?? undefined} />
        </>
      ) : tab === 'people' ? (
        <PeopleTab users={data.users} tenantId={tenantId} />
      ) : tab === 'health' ? (
        <AccountHealthSection tenantId={tenantId} />
      ) : (
        <AccountUsageSection detail={data} />
      )}
    </>
  );
}

/**
 * Product usage: are they getting value. The window every dated view reads is kept here, so switching between the
 * trend and the feature cards never silently resets the range you were looking at.
 */
export function AccountUsageSection({ detail }: { detail: AccountDetail }) {
  const [rangeDays, setRangeDays] = useState<number>(30);
  const { account, usage } = detail;
  const usedApps = new Set(account.apps_used);
  const unusedApps = account.apps_enabled.filter((a) => !usedApps.has(a));

  return (
    <>
      <div className="kpi-row">
        <StatTile label="Users" value={account.user_count}
          sub={account.active_user_count === null ? 'active count unknown' : `${account.active_user_count} active`} />
        <StatTile label="Events (30d)" value={account.event_count_30d} sub={`${formatCompact(account.event_count)} all time`} />
        <StatTile label="Logins" value={account.login_count} />
        <StatTile
          label="Pending invites"
          value={account.pending_invitation_count}
          tone={(account.pending_invitation_count ?? 0) > 0 ? 'warning' : 'default'}
        />
      </div>

      <SubscriptionCard account={account} />

      {/* An app enabled but never opened is a failed rollout — the single most actionable
          thing here, so it is called out rather than left for the reader to spot. */}
      {unusedApps.length > 0 && (
        <Callout tone="warning" title={`${unusedApps.length} app(s) enabled but never opened`}>
          {unusedApps.join(', ')} — switched on for this account with no recorded activity.
        </Callout>
      )}

      {account.last_activity_at === null && (
        <Callout tone="warning" title="This account has never done anything">
          Users exist but the audit log is empty — signed up and never used the product.
        </Callout>
      )}

      <ActivityTrendCard daily={detail.daily_activity} rangeDays={rangeDays} onRangeChange={setRangeDays} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 24 }}>
        <FeatureRankCard tenantId={account.tenant_id} rangeDays={rangeDays} />
        <ActionBreakdownCard usage={usage} />
      </div>
    </>
  );
}

/**
 * Health, risk and onboarding — the three things somebody opens a tenant to decide. A 404 means "never scored", a real
 * answer and not an error, so a missing panel is simply absent; when all three are, the page says so.
 */
export function AccountHealthSection({ tenantId }: { tenantId: string }) {
  const health = useQuery({ queryKey: ['tenant-health', tenantId], queryFn: () => api.scores.health(tenantId), retry: false });
  const risk = useQuery({ queryKey: ['tenant-risk', tenantId], queryFn: () => api.scores.risk(tenantId), retry: false });
  const onboarding = useQuery({ queryKey: ['tenant-onboarding', tenantId], queryFn: () => api.scores.onboarding(tenantId), retry: false });

  if (health.isPending || risk.isPending || onboarding.isPending) {
    return <div className="empty-state"><span className="spinner" aria-label="Loading" /></div>;
  }

  const failed = [health, risk, onboarding].some((q) => q.error instanceof ApiError && q.error.status !== 404);
  if (!health.data && !risk.data && !onboarding.data) {
    return (
      <div className="empty-state t-body">
        {failed ? 'The health scores could not be read.' : 'This tenant has not been scored yet — scores are computed after the next collector run.'}
      </div>
    );
  }

  return (
    <>
      {health.data && <HealthPanel health={health.data} />}
      {risk.data && <RiskPanel risk={risk.data} />}
      {onboarding.data && <OnboardingPanel onboarding={onboarding.data} />}
    </>
  );
}

/**
 * The commercial summary, from the REPLICA.
 *
 * On the Usage tab rather than only under Billing, because "what do they pay" is context for every
 * engagement number above it — 12k events is a different story on Starter than on Enterprise. It
 * reads from the replica so it renders with the platform down; the Billing tab is where live figures
 * and the ops actions live.
 */
function SubscriptionCard({ account }: { account: Account }) {
  const s = account.subscription;

  if (!s.is_replicated) {
    return (
      <Callout tone="warning" title="No subscription replicated for this account">
        IAM's feature gate <strong>fails open</strong> for a tenant with no subscription row, so this
        account has every app regardless of plan. Either the collector has not read{' '}
        <code>iam_global.subscriptions</code> yet, or the tenant genuinely has no row — the Billing tab
        asks IAM directly and can tell you which.
      </Callout>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Subscription</h2>
        <span className="t-label subtle">replicated — synced {formatWhen(account.synced_at)}</span>
      </div>
      <div
        className="card-body"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}
      >
        <Field label="Plan">
          <span className="mono">{s.plan_code}</span>
          {s.is_billable === false && <span className="badge-muted">not billed</span>}
        </Field>

        <Field label="Status"><StatusBadge subscription={s} /></Field>

        <Field label="Price">
          {formatMoney(s.price_minor, s.currency)}
          <span className="t-caption muted"> / {s.billing_cycle === 'annual' ? 'yr' : 'mo'}</span>
        </Field>

        <Field label="MRR">{formatMoney(account.mrr_minor, s.currency)}</Field>

        <Field label="Seats">
          <SeatsCell
            seatsUsed={account.seats_used}
            seatLimit={s.seat_limit}
            isAtLimit={account.is_at_seat_limit}
          />
          {account.seat_utilisation !== null && (
            <span className="t-caption muted"> {Math.round(account.seat_utilisation * 100)}%</span>
          )}
        </Field>

        {s.status === 'trialing' && (
          <Field label="Trial ends">
            {formatTrialCountdown(account.trial_days_remaining) ?? '—'}
            {(account.trial_days_remaining ?? 0) < 0 && (
              <span className="t-caption" style={{ color: 'var(--warning)' }}> lapsed</span>
            )}
          </Field>
        )}

        {s.current_period_end && (
          <Field label={s.cancel_at_period_end ? 'Ends' : 'Renews'}>
            {formatWhen(s.current_period_end)}
          </Field>
        )}

        {s.grace_period_ends_at && (
          <Field label="Grace ends">{formatWhen(s.grace_period_ends_at)}</Field>
        )}

        {s.retention_until && (
          <Field label="Purge-eligible">{formatWhen(s.retention_until)}</Field>
        )}

        <Field label="Entitled apps">
          {s.apps_entitled.length === 0 ? (
            <span className="subtle">none</span>
          ) : (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {s.apps_entitled.map((a) => (
                <span key={a} className="chip chip-on">{a}</span>
              ))}
            </span>
          )}
        </Field>

        {s.overrides.length > 0 && (
          <Field label="Overrides">
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {s.overrides.map((o) => (
                <span
                  key={o.app_key}
                  className={o.effect === 'deny' ? 'chip chip-down' : 'chip chip-on'}
                >
                  {o.effect} {o.app_key}
                </span>
              ))}
            </span>
          </Field>
        )}
      </div>

      {/* The three app lists exist to expose exactly these two gaps. Called out rather than left for
          the reader to diff three chip rows by eye. */}
      {account.entitled_unused_apps.length > 0 && (account.mrr_minor ?? 0) > 0 && (
        <div className="card-body" style={{ paddingTop: 0 }}>
          <Callout tone="info" title="Paying for apps they never opened">
            {account.entitled_unused_apps.join(', ')} — entitled by the plan, no recorded activity.
            A failed rollout, or a downgrade waiting to happen.
          </Callout>
        </div>
      )}

      {account.drifted_apps.length > 0 && (
        <div className="card-body" style={{ paddingTop: 0 }}>
          <Callout tone="warning" title="tenant_apps has drifted from the subscription">
            {account.drifted_apps.join(', ')} — switched on in the tenant's own <code>tenant_apps</code>{' '}
            but not entitled. Harmless for access (IAM's gate reads the global store, not this), but the
            app launcher will show them and clicking one yields a 403. IAM's reconcile sweep repairs it.
          </Callout>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="t-label muted" style={{ marginBottom: 2 }}>{label}</div>
      <div className="t-body">{children}</div>
    </div>
  );
}


/** The action breakdown as a table — the relief channel guaranteeing every value is readable. */
function ActionBreakdownCard({ usage }: { usage: AccountUsage[] }) {
  if (usage.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>What they do</h2>
        </div>
        <div className="empty-state t-body">No recorded activity.</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>What they do</h2>
        <span className="t-label subtle">{usage.length} action types</span>
      </div>
      <table className="table">
        <thead>
          <tr><th>App</th><th>Action</th><th className="num">Count</th></tr>
        </thead>
        <tbody>
          {usage.slice(0, 25).map((u) => (
            <tr key={`${u.app_key}:${u.action}`}>
              <td><span className="mono t-label">{u.app_key}</span></td>
              {/* Audit actions are i18n keys (audit.action.created); show the readable tail. */}
              <td>{u.action.replace(/^audit\.action\./, '')}</td>
              <td className="num">{formatCompact(u.event_count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Fixed windows. Custom dates are deliberately absent — nobody asks "how did week 3 of July go". */
const RANGE_OPTIONS = [7, 30, 90] as const;

function RangePicker({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <div style={{ display: 'inline-flex', gap: 4 }} role="group" aria-label="Date range">
      {RANGE_OPTIONS.map((d) => (
        <button
          key={d}
          type="button"
          className={`btn ${days === d ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => onChange(d)}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

/**
 * Everyone in the tenant, with what they are allowed to do and whether they ever showed up.
 *
 * Sorted by activity, but the two columns that matter commercially are Roles and First login: a
 * seat held by someone who has never signed in is a failed rollout, and it is invisible on a user
 * count. Owner and operator are called out because "who do I actually contact" and "who answers
 * chats" are the two questions asked of this table most often.
 */
/** <c>users</c> null = the role may not see named people (telemetry:agent_detail); the aggregates still show. */
export function PeopleTab({ users, tenantId }: { users: AccountUser[] | null; tenantId: string }) {
  // Three independent reads rather than one bundle: presence is the headline and must render even
  // if the seat list or the device mix fails, and each is cheap enough that splitting them costs
  // nothing. A viewer without agent_detail still gets all three — none of them names a session.
  const presence = useQuery({
    queryKey: ['presence', tenantId],
    queryFn: () => api.presence.tenant(tenantId),
    retry: false,
  });

  const seats = useQuery({
    queryKey: ['seats', tenantId],
    queryFn: () => api.presence.seats(tenantId),
    retry: false,
  });

  const devices = useQuery({
    queryKey: ['tenant-devices', tenantId],
    queryFn: () => api.presence.tenantDevices(tenantId),
    retry: false,
  });

  const flows = useQuery({
    queryKey: ['tenant-paths', tenantId],
    queryFn: () => api.paths.tenant(tenantId),
    retry: false,
  });

  const collaboration = useQuery({
    queryKey: ['tenant-collaboration', tenantId],
    queryFn: () => api.collaboration.tenant(tenantId),
    retry: false,
  });

  const configuration = useQuery({
    queryKey: ['tenant-configuration', tenantId],
    queryFn: () => api.collaboration.configuration(tenantId),
    retry: false,
  });

  // Live state lives on the seat rows, keyed for the table below.
  const liveByUser = new Map((seats.data ?? []).map((s) => [s.user_id, s]));

  // Only agents the API positively identified as isolated. An empty set when the query has not
  // returned, or when sharing was never collected — in both cases no row gets marked, which is the
  // honest default for a claim about a named person.
  const isolatedUsers = new Set(
    (collaboration.data?.isolated ?? []).map((a) => a.user_id),
  );

  if (users === null) {
    // Withheld for this role — the aggregates above the list are not person-level and still show.
    return (
      <>
        {presence.data && <PresenceStrip presence={presence.data} />}
        <div className="empty-state t-body">The list of people (names, e-mails, devices) is shown to owners and analysts only.</div>
      </>
    );
  }

  if (users.length === 0) {
    return <div className="empty-state t-body">No users replicated for this account yet.</div>;
  }

  return (
    <>
      {presence.data && <PresenceStrip presence={presence.data} />}

      {/* Two KPI rows sat here and said overlapping things — a screenshot made it obvious in a way
          the code never did. "Never signed in" appeared in BOTH rows, and this row's "Active"
          (account status is enabled) sat inches from presence's "Active today" (signed in since
          midnight) meaning something entirely different.

          What stays is what presence does NOT cover: the roster count, who answers chats, and how
          many accounts are enabled — relabelled so it cannot be read as an activity number. When
          presence is unavailable the strip above renders a stated absence, so these still stand
          alone. */}
      <div className="kpi-row">
        <StatTile label="People" value={users.length} sub="on the roster" />
        <StatTile label="Operators" value={users.filter((u) => u.is_operator).length} sub="answer SalesIQ chats" />
        <StatTile
          label="Enabled accounts"
          value={users.filter((u) => u.status === 'active').length}
          sub="not a sign-in count"
        />
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>People</h2>
          <span className="t-label subtle">most active first</span>
        </div>
        <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Presence</th>
              <th>Roles</th>
              <th>Status</th>
              <th className="num">Events</th>
              <th>First login</th>
              <th>Last active</th>
              <th>Device</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id}>
                <td>
                  <div style={{ fontWeight: 600 }}>
                    {/* The name is the way into one person's session history. Nothing else on this
                        page links there, and the per-user route is gated on agent_detail — a viewer
                        following this link gets a stated 403, not a blank page. */}
                    <Link to={`/accounts/${tenantId}/users/${u.user_id}`}>
                      {u.display_name || <span className="subtle">(no name)</span>}
                    </Link>
                    {u.is_owner && <span className="badge-new">OWNER</span>}
                    {u.is_operator && <span className="badge-muted">operator</span>}
                    {/* Paired with the word, never colour alone — and rendered only when the flag is
                        genuinely TRUE. A null means the share tables were not read, and marking
                        somebody as working alone on that basis would be a claim we cannot support. */}
                    {isolatedUsers.has(u.user_id) && (
                      <span className="chip chip-down" title="Shares nothing and receives nothing">
                        works alone
                      </span>
                    )}
                  </div>
                  <div className="t-caption muted">{u.email || '—'}</div>
                </td>
                <td>
                  {/* Absent seat data is not "offline" — that would claim the person is not signed
                      in when the truth is that nothing was collected about them. */}
                  {liveByUser.has(u.user_id)
                    ? <LiveDot
                        live={liveByUser.get(u.user_id)!.is_live}
                        label={liveByUser.get(u.user_id)!.is_live
                          ? 'Live'
                          : u.last_active_at ? formatWhen(u.last_active_at) : 'never'}
                      />
                    : <span className="subtle">—</span>}
                </td>
                <td><RoleChips roles={u.roles} /></td>
                <td style={{ color: u.status === 'active' ? 'var(--success)' : 'var(--text-subtle)' }}>
                  {u.status || '—'}
                </td>
                <td className="num">{formatCompact(u.event_count)}</td>
                <td className="muted">
                  {u.first_login_at
                    ? formatWhen(u.first_login_at)
                    /* Not "never" in muted grey like an empty cell — this one is a finding. */
                    : <span style={{ color: 'var(--warning)' }}>never signed in</span>}
                </td>
                <td className="muted">
                  {u.last_active_at ? formatWhen(u.last_active_at) : <span className="subtle">never</span>}
                </td>
                <td className="muted t-caption">{u.last_device || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {seats.data && <DeadSeatsPanel seats={seats.data} tenantId={tenantId} />}

      {devices.data && <DeviceMixPanel mix={devices.data} title="What they sign in from" />}

      {flows.data && <FlowsPanel flows={flows.data} title="How they move through the product" />}

      {collaboration.data && <TeamPanel collaboration={collaboration.data} />}

      {configuration.data && <ConfigurationPanel configuration={configuration.data} />}
    </>
  );
}

/**
 * Roles as `app:Profile`. The app prefix is shown separately and de-emphasised: the same profile
 * name means different things in different apps, so dropping it would merge `crm:Admin` and
 * `salesiq:Admin` into one indistinguishable "Admin".
 */
function RoleChips({ roles }: { roles: string[] }) {
  if (roles.length === 0) return <span className="subtle">no role</span>;

  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {roles.map((r) => {
        const [app, ...rest] = r.split(':');
        const name = rest.join(':') || r;
        // `-` is the tenant-wide Owner, which belongs to no app. Showing a literal dash as though
        // it were an app name would be nonsense, so it is simply omitted.
        const scope = app === '-' ? null : app;
        return (
          <span key={r} className="chip chip-on" title={r}>
            {scope && <span style={{ opacity: 0.65 }}>{scope} </span>}
            {name}
          </span>
        );
      })}
    </span>
  );
}

/** Cuts a dated series to the last N days, counted from the newest row PRESENT. */
function withinRange<T extends { date: string }>(rows: T[], days: number): T[] {
  if (rows.length === 0) return rows;
  // Anchored to the latest collected day, NOT to today. The collector runs on a schedule, so
  // anchoring to `now` silently empties the chart whenever a run is late — showing "no activity"
  // for what is really "no data yet", which are opposite conclusions.
  const newest = rows.reduce((max, r) => (r.date > max ? r.date : max), rows[0]!.date);
  const cutoff = new Date(`${newest}T00:00:00Z`);
  cutoff.setUTCDate(cutoff.getUTCDate() - (days - 1));
  const from = cutoff.toISOString().slice(0, 10);
  return rows.filter((r) => r.date >= from);
}

/** Events and logins per day, with the range control that drives every dated view on the page. */
function ActivityTrendCard({
  daily,
  rangeDays,
  onRangeChange,
}: {
  daily: AccountDailyActivity[];
  rangeDays: number;
  onRangeChange: (d: number) => void;
}) {
  const rows = useMemo(() => withinRange(daily, rangeDays), [daily, rangeDays]);

  const series = useMemo(
    () => [
      {
        key: 'events',
        label: 'Events',
        color: 'var(--series-1)',
        points: rows.map((r) => ({ date: r.date, value: r.events })),
      },
      {
        key: 'logins',
        label: 'Logins',
        color: 'var(--series-2)',
        points: rows.map((r) => ({ date: r.date, value: r.logins })),
      },
    ],
    [rows],
  );

  const totalEvents = rows.reduce((sum, r) => sum + r.events, 0);
  const peak = rows.reduce((max, r) => Math.max(max, r.active_users), 0);

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Activity over time</h2>
        <div style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
          <span className="t-label subtle">
            {formatCompact(totalEvents)} events · peak {peak} active users/day
          </span>
          <RangePicker days={rangeDays} onChange={onRangeChange} />
        </div>
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          <div className="empty-state t-body">
            No daily activity replicated yet. Run the collector to populate it.
          </div>
        ) : (
          <TrendChart series={series} height={240} />
        )}
      </div>
    </div>
  );
}

/**
 * Most-used features for the chosen window, from the telemetry ranking (`GET /accounts/{id}/features`). A FEATURE is
 * the (app, thing acted on) pair, named from the feature registry; an unnamed one is flagged, never hidden. (This card
 * used to rank the account's (app, action) rows, which carry no feature — it could only ever say "created, updated".)
 */
function FeatureRankCard({ tenantId, rangeDays }: { tenantId: string; rangeDays: number }) {
  const canRead = hasPermission(PERMISSIONS.telemetryRead);
  const day = (offset: number) => new Date(Date.now() - offset * 86_400_000).toISOString().slice(0, 10);
  const features = useQuery({
    queryKey: ['tenant-features', tenantId, rangeDays],
    queryFn: () => api.telemetry.tenantFeatures(tenantId, day(rangeDays - 1), day(0)),
    enabled: canRead,
    retry: false,
  });

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Most used features</h2>
        <span className="t-label subtle">last {rangeDays} days</span>
      </div>
      <div className="card-body">
        {!canRead ? (
          <div className="empty-state t-body">Needs the telemetry permission.</div>
        ) : features.isPending ? (
          <div className="empty-state"><span className="spinner" aria-label="Loading" /></div>
        ) : features.isError ? (
          <div className="empty-state t-body note-error">The feature ranking could not be read.</div>
        ) : (
          <BarList
            rows={(features.data ?? []).map((f) => ({
              label: f.is_known ? f.display_name : `${f.display_name} (unnamed)`,
              value: f.event_count,
            }))}
            limit={10}
            emptyLabel="No feature usage in this range."
          />
        )}
      </div>
    </div>
  );
}

